import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const COUNT_KEY = "install-prompt-shown-count";
const DISMISSED_KEY = "install-prompt-dismissed";
const INSTALLED_KEY = "install-prompt-installed";
const MAX_SHOWS = 2;

export function InstallAppPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Only on mobile
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;

    // Already installed / running standalone?
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // @ts-expect-error iOS Safari
      window.navigator.standalone === true;
    if (standalone) return;

    try {
      if (window.localStorage.getItem(INSTALLED_KEY) === "1") return;
      if (window.localStorage.getItem(DISMISSED_KEY) === "1") return;
      const count = Number(window.localStorage.getItem(COUNT_KEY) || "0");
      if (count >= MAX_SHOWS) return;
    } catch {
      return;
    }

    const ua = window.navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
    setIsIOS(ios);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      try {
        window.localStorage.setItem(INSTALLED_KEY, "1");
      } catch {
        // ignore
      }
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // Show after a short delay so page paints first
    const t = window.setTimeout(() => {
      // Only show on iOS or when we have a deferred prompt (Android/Chrome)
      if (ios) {
        setVisible(true);
        try {
          const count = Number(window.localStorage.getItem(COUNT_KEY) || "0");
          window.localStorage.setItem(COUNT_KEY, String(count + 1));
        } catch {
          // ignore
        }
      }
    }, 2500);

    return () => {
      window.clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // When a deferred prompt arrives (Android), show our sheet & bump counter
  useEffect(() => {
    if (!deferred) return;
    setVisible(true);
    try {
      const count = Number(window.localStorage.getItem(COUNT_KEY) || "0");
      window.localStorage.setItem(COUNT_KEY, String(count + 1));
    } catch {
      // ignore
    }
  }, [deferred]);

  const close = () => setVisible(false);

  const dismissForever = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // ignore
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        window.localStorage.setItem(INSTALLED_KEY, "1");
      }
    } catch {
      // ignore
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 px-4 pb-4 md:hidden">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-accent/25 bg-background shadow-2xl">
        <div className="flex items-start gap-3 p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/15 text-accent">
            <Download size={22} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg leading-tight">
              Installera <span className="font-italic-display text-accent">AK Husbil</span> som app
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Snabbare start, ikon på hemskärmen och en renare vy — helt gratis.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="-mr-1 -mt-1 rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </div>

        {isIOS ? (
          <div className="border-t border-border bg-secondary/40 px-5 py-4 text-sm">
            <p className="flex items-center gap-1.5 font-medium">
              <Share size={15} className="text-accent" /> På iPhone
            </p>
            <p className="mt-1 text-muted-foreground">
              Tryck på <b>Dela</b>-knappen i Safari och välj <b>Lägg till på hemskärmen</b>.
            </p>
          </div>
        ) : null}

        <div className="flex gap-2 border-t border-border p-3">
          <button
            type="button"
            onClick={dismissForever}
            className="flex-1 rounded-full border border-border bg-background px-4 py-3 text-sm font-medium text-foreground hover:bg-secondary"
          >
            Avbryt
          </button>
          {!isIOS && deferred ? (
            <button
              type="button"
              onClick={install}
              className="flex-1 rounded-full bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Ladda ner
            </button>
          ) : (
            <button
              type="button"
              onClick={close}
              className="flex-1 rounded-full bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Okej
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
